import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { logAuditAction } from './auditLogger';

const VOICE_FEATURE_STORAGE_KEY = 'azuraos_feature_voice_transcriber';
const FIRESTORE_SWITCHES_PATH = doc(db, 'system', 'operational_switches');
const FEATURE_FLAGS_EVENT = 'azuraos_feature_flags_updated';

let inMemoryVoiceEnabled: boolean = false;
let isInitialized: boolean = false;

// Safe localStorage wrapper
function safeGetStorage(key: string): string | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSetStorage(key: string, value: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Ignore storage quota errors in restricted environments
  }
}

/**
 * Checks if the Voice Transcriber & Audio Studio is currently enabled.
 * Default is FALSE (dormant for Phase 2 residency roadmap / accelerator interviews).
 * Can also be dynamically bypassed with ?voice=true or ?audio=true in URL for emergency private demoing.
 */
export function isVoiceTranscriberEnabled(): boolean {
  // 1. Secret URL query parameter override for quick founder inspection
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('voice') === 'true' || params.get('audio') === 'true') {
        return true;
      }
    } catch {
      // Ignore URL parsing errors
    }
  }

  // 2. Local storage check
  const local = safeGetStorage(VOICE_FEATURE_STORAGE_KEY);
  if (local === 'true') return true;
  if (local === 'false') return false;

  // 3. In-memory Firestore cache
  return inMemoryVoiceEnabled;
}

/**
 * Super Admin setter: toggles the Voice Transcriber feature flag.
 * Syncs to localStorage, Firestore (system/operational_switches), and triggers a local browser event.
 */
export async function setVoiceTranscriberEnabled(enabled: boolean): Promise<void> {
  inMemoryVoiceEnabled = enabled;
  safeSetStorage(VOICE_FEATURE_STORAGE_KEY, enabled ? 'true' : 'false');

  // Dispatch event so all open views immediately re-render
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FEATURE_FLAGS_EVENT, { detail: { voiceEnabled: enabled } }));
  }

  try {
    await setDoc(FIRESTORE_SWITCHES_PATH, {
      voiceTranscriberEnabled: enabled,
      updatedAt: new Date().toISOString()
    }, { merge: true });

    logAuditAction({
      actionType: 'UPDATE_SETTINGS',
      entityType: 'System',
      details: `Phase 2 Voice Transcriber & Audio Studio set to ${enabled ? 'ACTIVATED (Visible)' : 'DORMANT (Hidden for Phase 2 Roadmap)'}`,
      performedBy: 'super-admin'
    });
  } catch (err) {
    console.warn('[FeatureFlags] Could not sync to Firestore switches:', err);
  }
}

/**
 * React Hook to dynamically listen to Voice Feature Flag state across components
 */
export function useVoiceFeatureFlag(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => isVoiceTranscriberEnabled());

  useEffect(() => {
    // Initial sync from Firestore once per app load
    if (!isInitialized) {
      isInitialized = true;
      try {
        getDoc(FIRESTORE_SWITCHES_PATH).then((snap) => {
          if (snap.exists()) {
            const data = snap.data();
            if (typeof data.voiceTranscriberEnabled === 'boolean') {
              inMemoryVoiceEnabled = data.voiceTranscriberEnabled;
              // If not explicitly set in localStorage, adopt Firestore remote flag
              if (safeGetStorage(VOICE_FEATURE_STORAGE_KEY) === null) {
                setEnabled(data.voiceTranscriberEnabled);
              }
            }
          }
        }).catch(() => {});
      } catch {
        // Ignore network errors
      }
    }

    const handleUpdate = (e: Event) => {
      const custom = e as CustomEvent<{ voiceEnabled?: boolean }>;
      if (custom.detail && typeof custom.detail.voiceEnabled === 'boolean') {
        setEnabled(custom.detail.voiceEnabled);
      } else {
        setEnabled(isVoiceTranscriberEnabled());
      }
    };

    window.addEventListener(FEATURE_FLAGS_EVENT, handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener(FEATURE_FLAGS_EVENT, handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  return enabled;
}
